'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components/Button';
import { cn } from '../../utils/cn';
import { IconLoadingCircle } from '../icons/IconLoadingCircle.ts';
import { IconLoadingCircleDark } from '../icons/IconLoadingCircleDark.ts';

export const baseButtonClasses = cn(`
    flex cursor-pointer items-center justify-center gap-lr-spacing-100 rounded-lr-main px-lr-spacing-200
    text-lr-text-main outline-lr-border-focus transition
    data-disabled:text-lr-text-disabled
    data-focus-visible:outline-lr-focus data-focus-visible:outline-offset-lr-focus
    data-hovered:shadow-lr-medium
    data-pressed:shadow-none
`);

export const buttonVariants = cva(baseButtonClasses, {
    variants: {
        size: {
            xs: 'size-lr-size-400 px-lr-spacing-100 py-lr-spacing-075 text-lr-button-xs',
            sm: 'size-lr-size-500 px-lr-spacing-150 text-lr-button-sm',
            md: 'size-lr-size-600 text-lr-button-md'
        },
        icon: {
            false: 'w-auto',
            true: `
                px-lr-spacing-150 text-lr-icon-main
                data-disabled:text-lr-icon-disabled
            `
        },
        pill: {
            false: '',
            true: 'rounded-lr-full'
        },
        onBackground: {
            false: '',
            true: ['text-lr-text-main-inverse', 'data-disabled:text-lr-text-disabled-inverse']
        },
        isLaRedoutePlus: {
            false: '',
            true: ''
        },
        variant: {
            primary: `
                bg-lr-bg-button-primary-default text-lr-text-main-inverse
                data-disabled:bg-lr-bg-button-primary-disabled data-disabled:text-lr-text-disabled-inverse
                data-hovered:bg-lr-bg-button-primary-hover
                data-pressed:bg-lr-bg-button-primary-pressed
            `,
            secondary: `
                bg-lr-bg-button-secondary-default
                data-disabled:bg-lr-bg-button-secondary-disabled
                data-hovered:bg-lr-bg-button-secondary-hover
                data-pressed:bg-lr-bg-button-secondary-pressed
            `,
            tertiary: `
                border-lr-main border-lr-border-main bg-lr-bg-button-tertiary-default backdrop-blur-lr-soft-blur
                data-disabled:border-lr-border-disabled data-disabled:bg-lr-bg-button-tertiary-disabled
                data-hovered:border-lr-border-hover data-hovered:bg-lr-bg-button-tertiary-hover
                data-pressed:border-lr-border-active data-pressed:bg-lr-bg-button-tertiary-pressed
            `,
            plain: `
                data-hovered:bg-lr-bg-button-plain-hover
                data-pressed:bg-lr-bg-button-plain-pressed
            `
        }
    },
    compoundVariants: [
        {
            variant: 'primary',
            onBackground: true,
            className: cn(`
                bg-lr-bg-button-primary-default-inverse text-lr-text-main
                data-disabled:bg-lr-bg-button-primary-disabled-inverse data-disabled:text-lr-text-disabled
                data-hovered:bg-lr-bg-button-primary-hover-inverse
                data-pressed:bg-lr-bg-button-primary-pressed-inverse
            `)
        },
        {
            variant: 'secondary',
            onBackground: true,
            className: cn(`
                bg-lr-bg-button-secondary-default-inverse
                data-disabled:bg-lr-bg-button-secondary-disabled-inverse
                data-hovered:bg-lr-bg-button-secondary-hover-inverse
                data-pressed:bg-lr-bg-button-secondary-pressed-inverse
            `)
        },
        {
            variant: 'tertiary',
            onBackground: true,
            className: cn(`
                border-lr-main border-lr-border-subtle bg-lr-bg-button-tertiary-default-inverse
                data-disabled:border-lr-border-main data-disabled:bg-lr-bg-button-tertiary-disabled-inverse
                data-hovered:border-lr-border-light data-hovered:bg-lr-bg-button-tertiary-hover-inverse
                data-pressed:border-lr-border-main-inverse data-pressed:bg-lr-bg-button-tertiary-pressed-inverse
            `)
        },
        {
            variant: 'plain',
            onBackground: true,
            className: cn(`
                data-hovered:bg-lr-bg-button-plain-hover-inverse
                data-pressed:bg-lr-bg-button-plain-pressed-inverse
            `)
        },
        {
            variant: 'primary',
            onBackground: false,
            isLaRedoutePlus: true,
            className: cn(`
                bg-lrplus-bg-button-primary-default
                data-disabled:bg-lrplus-bg-button-primary-disabled
                data-hovered:bg-lrplus-bg-button-primary-hover
                data-pressed:bg-lrplus-bg-button-primary-pressed
            `)
        },
        {
            icon: true,
            onBackground: true,
            className: cn(`
                text-lr-icon-main-inverse
                data-disabled:text-lr-icon-disabled-inverse
            `)
        },
        {
            variant: 'primary',
            onBackground: true,
            icon: true,
            className: cn(`
                text-lr-icon-main
                data-disabled:text-lr-icon-disabled
            `)
        },
        {
            variant: 'primary',
            onBackground: false,
            icon: true,
            className: cn(`
                text-lr-icon-main-inverse
                data-disabled:text-lr-icon-disabled-inverse
            `)
        },
        {
            size: 'sm',
            icon: true,
            className: 'px-lr-spacing-100'
        },
        {
            size: 'xs',
            icon: true,
            className: 'px-lr-spacing-050'
        }
    ],
    defaultVariants: {
        size: 'md',
        icon: false,
        pill: false,
        onBackground: false,
        isLaRedoutePlus: false,
        variant: 'primary'
    }
});

export interface ButtonInteractionOptions {
    isDisabled?: boolean;
    isPending?: boolean;
    onClick?: Parameters<typeof AriaButton>[0]['onClick'];
}
type ButtonInteractionHandlers = {
    handleClick: Parameters<typeof AriaButton>[0]['onClick'];
    handleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
};
export function createButtonInteractionHandlers({
    isDisabled,
    isPending,
    onClick
}: ButtonInteractionOptions): ButtonInteractionHandlers {
    const handleClick: Parameters<typeof AriaButton>[0]['onClick'] = (event): void => {
        event.stopPropagation();

        if (isDisabled || isPending) {
            event.preventDefault();
            return;
        }

        if (onClick) {
            onClick(event);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        if ((isDisabled || isPending) && (event.key === ' ' || event.key === 'Enter')) {
            event.preventDefault();
        }
        (
            event as React.KeyboardEvent<HTMLButtonElement> & { continuePropagation?: () => void }
        ).continuePropagation?.();
    };

    return { handleClick, handleKeyDown };
}

export interface ButtonProps extends AriaButtonProps, VariantProps<typeof buttonVariants> {
    ref?: React.Ref<HTMLButtonElement> | React.RefObject<HTMLButtonElement>;
}

function Button({
    className,
    size,
    icon,
    pill,
    onBackground,
    isLaRedoutePlus,
    variant,
    isPending,
    ref,
    children,
    onClick,
    type = 'button',
    ...rest
}: ButtonProps) {
    const { handleClick, handleKeyDown } = createButtonInteractionHandlers({
        isDisabled: rest?.isDisabled,
        isPending,
        onClick: onClick as ((e: React.MouseEvent) => void) | undefined
    });

    return isPending ? (
        <AriaButton
            className={cn(
                buttonVariants({ size, icon: true, pill, onBackground, isLaRedoutePlus, variant }),
                className
            )}
            {...rest}
            ref={ref}
            isPending={true}
        >
            {!onBackground ? (
                <IconLoadingCircle className="animate-reverse-spin" size="xs" />
            ) : (
                <IconLoadingCircleDark className="animate-reverse-spin" size="xs" />
            )}
        </AriaButton>
    ) : (
        <AriaButton
            className={cn(buttonVariants({ size, icon, pill, onBackground, isLaRedoutePlus, variant }), className)}
            ref={ref}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            type={type}
            {...rest}
        >
            {children}
        </AriaButton>
    );
}

export { Button };
export default Button;
